/**
 * Panel Visibility Commands - Sidebar and chat panel open/close/toggle
 *
 * Controls the visibility of the sidebar and chat panels within a space.
 * All state lives in spaceStore and is persisted to the backend.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useSpaceStore from '../../stores/spaceStore';

const log = createFileLogger('PANEL_COMMANDS', { enabled: true });

export const panelVisibilityCommands: SelfContainedCommand[] = [
  // ====== SIDEBAR ======
  {
    id: 'space_open_sidebar',
    description: 'Open the sidebar panel in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Sidebar opened', { isSidebarVisible: true }),
          description: 'Open the sidebar',
          context: 'When user wants to see the file tree'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setSidebarVisible, isSidebarVisible } = useSpaceStore.getState();
      log.log('[space_open_sidebar] current:', isSidebarVisible);

      if (isSidebarVisible) {
        return createSuccessResult('Sidebar is already open', { isSidebarVisible: true }, {
          metadata: { executionTime: Date.now() - startTime }
        });
      }

      await setSidebarVisible(true);
      log.log('[space_open_sidebar] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult('✅ Sidebar opened', { isSidebarVisible: true }, {
        metadata: { executionTime: Date.now() - startTime }
      });
    }
  } as SelfContainedCommand,

  {
    id: 'space_close_sidebar',
    description: 'Close the sidebar panel in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Sidebar closed', { isSidebarVisible: false }),
          description: 'Close the sidebar',
          context: 'When user wants more window space'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setSidebarVisible, isSidebarVisible } = useSpaceStore.getState();
      log.log('[space_close_sidebar] current:', isSidebarVisible);

      if (!isSidebarVisible) {
        return createSuccessResult('Sidebar is already closed', { isSidebarVisible: false }, {
          metadata: { executionTime: Date.now() - startTime }
        });
      }

      await setSidebarVisible(false);
      log.log('[space_close_sidebar] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult('✅ Sidebar closed', { isSidebarVisible: false }, {
        metadata: { executionTime: Date.now() - startTime }
      });
    }
  } as SelfContainedCommand,

  {
    id: 'space_toggle_sidebar',
    description: 'Toggle the sidebar panel visibility in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Sidebar toggled', { isSidebarVisible: true }),
          description: 'Toggle sidebar open or closed',
          context: 'When user wants to flip sidebar visibility'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setSidebarVisible, isSidebarVisible } = useSpaceStore.getState();
      const newValue = !isSidebarVisible;
      log.log('[space_toggle_sidebar] current:', isSidebarVisible, '→', newValue);

      await setSidebarVisible(newValue);
      log.log('[space_toggle_sidebar] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult(
        `✅ Sidebar ${newValue ? 'opened' : 'closed'}`,
        { isSidebarVisible: newValue },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  } as SelfContainedCommand,

  // ====== CHAT PANEL ======
  {
    id: 'space_open_chat',
    description: 'Open the AI chat panel in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Chat panel opened', { isChatVisible: true }),
          description: 'Open the chat panel',
          context: 'When user wants to talk to the AI'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setIsChatVisible, isChatVisible } = useSpaceStore.getState();
      log.log('[space_open_chat] current:', isChatVisible);

      if (isChatVisible) {
        return createSuccessResult('Chat panel is already open', { isChatVisible: true }, {
          metadata: { executionTime: Date.now() - startTime }
        });
      }

      await setIsChatVisible(true);
      log.log('[space_open_chat] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult('✅ Chat panel opened', { isChatVisible: true }, {
        metadata: { executionTime: Date.now() - startTime }
      });
    }
  } as SelfContainedCommand,

  {
    id: 'space_close_chat',
    description: 'Close the AI chat panel in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Chat panel closed', { isChatVisible: false }),
          description: 'Close the chat panel',
          context: 'When user wants more window space without the chat'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setIsChatVisible, isChatVisible } = useSpaceStore.getState();
      log.log('[space_close_chat] current:', isChatVisible);

      if (!isChatVisible) {
        return createSuccessResult('Chat panel is already closed', { isChatVisible: false }, {
          metadata: { executionTime: Date.now() - startTime }
        });
      }

      await setIsChatVisible(false);
      log.log('[space_close_chat] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult('✅ Chat panel closed', { isChatVisible: false }, {
        metadata: { executionTime: Date.now() - startTime }
      });
    }
  } as SelfContainedCommand,

  {
    id: 'space_toggle_chat',
    description: 'Toggle the AI chat panel visibility in the current space',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('Chat panel toggled', { isChatVisible: true }),
          description: 'Toggle chat panel open or closed',
          context: 'When user wants to flip chat visibility'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { setIsChatVisible, isChatVisible } = useSpaceStore.getState();
      const newValue = !isChatVisible;
      log.log('[space_toggle_chat] current:', isChatVisible, '→', newValue);

      await setIsChatVisible(newValue);
      log.log('[space_toggle_chat] Done, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult(
        `✅ Chat panel ${newValue ? 'opened' : 'closed'}`,
        { isChatVisible: newValue },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  } as SelfContainedCommand,
];
